/**
 * 气泡消息模型
 */
export class BubbleMessage {
  /**
   * @type {MessageModel[]}
   */
  messageList = [];
  constructor(params) {
    if (params) {
      Object.assign(this, params);
    }
  }

  /**
   * 根据 sequenceNumber 判断追加还是覆盖
   * @param {MessageModel} msg
   */
  addOrUpdateMessage(msg) {
    this.messageList.push(msg);
  }

  /**
   * 返回用于渲染的气泡列表：
   * - 每个气泡为一个MessageModel（已完成的或合并后的未完成流式消息）
   * - 保证每个气泡都带有content/files/tool_interactions/avatarUrl等渲染字段
   */
  /**
   * 只保留每组sequenceNumber的最后一条（即已完成的消息），丢弃同组未完成的流式消息，避免内容重复。
   */
  getMessages() {
    const result = [];
    const seqMap = new Map();
    for (const msg of this.messageList) {
      // 只保留每个sequenceNumber的最后一条（优先isCompleted）
      if (!seqMap.has(msg.sequenceNumber)) {
        seqMap.set(msg.sequenceNumber, msg);
      } else {
        // 如果已存在，优先用isCompleted的覆盖
        const prev = seqMap.get(msg.sequenceNumber);
        if (!prev.isCompleted && msg.isCompleted) {
          seqMap.set(msg.sequenceNumber, msg);
        } else if (!prev.isCompleted && !msg.isCompleted) {
          // 都是未完成，取最新
          let content = "";
          const uncompletedSequenceNumberMessages = this.messageList.filter(
            (m) => m.sequenceNumber === msg.sequenceNumber && !m.isCompleted
          );
          for (const m of uncompletedSequenceNumberMessages) {
            content += this._extractText(m);
          }

          const finalMsg = { ...msg, content };
          seqMap.set(msg.sequenceNumber, finalMsg);
        } else if (prev.isCompleted && !msg.isCompleted) {
          // 已完成优先
          // do nothing
        } else if (prev.isCompleted && msg.isCompleted) {
          // 都是已完成，取最新
          seqMap.set(msg.sequenceNumber, msg);
        }
      }
    }
    // 按时间排序
    result.push(
      ...Array.from(seqMap.values()).sort((a, b) => a.timestamp - b.timestamp)
    );
    return result;
  }

  /**
   * 获取气泡的文本消息内容
   * - 如果最后一条消息 isCompleted，则只返回最后一条内容
   * - 如果不是，则拼接所有 isStream 的消息内容
   * @returns {string}
   */
  getTextMessage() {
    console.log("Getting text message from bubble:", this);
    if (this.messageList.length === 0) return "";
    const lastMsg = this.messageList[this.messageList.length - 1];
    if (lastMsg.isCompleted) {
      // 返回最后一条完整消息内容
      return this._extractText(lastMsg);
    } else {
      // 拼接所有流式消息内容
      return this.messageList
        .filter((m) => !m.isCompleted)
        .map((m) => this._extractText(m))
        .join("");
    }
  }

  /**
   * 提取消息文本内容
   * @param {MessageModel} msg
   * @returns {string}
   */
  _extractText(msg) {
    if (msg.message && typeof msg.message.content === "string") {
      return msg.message.content;
    }
    if (typeof msg.content === "string") {
      return msg.content;
    }
    if (Array.isArray(msg.content)) {
      // 兼容数组内容
      return msg.content.map((c) => c.content || "").join("");
    }
    return "";
  }
}
export const MessageContentType = {
  SENDING: "SENDING",
  SENT: "SENT",
  FAILED: "FAILED",
  MULTIPLESELECT: "MULTIPLESELECT",
  QRCODE: "QRCODE",
  CHARTS: "CHARTS",
  ECHARTS: "ECHARTS",
  HTML: "HTML",
  URL: "URL",
  STREAMING: "STREAMING",
  RULEGOJSON: "RULEGOJSON",
  TEXT: "TEXT",
};

export const MessageSourceType = {
  USER: "USER",
  AI: "AI",
  SYSTEM: "SYSTEM",
};

/**
 * 消息模型
 */
export class MessageModel {
  conversationId = "";
  // Properties
  sequenceNumber = 0;
  /**
   * Example content formats:
   * [{
   *   "type": "text",
   *   "content": "I see you've uploaded a file named \"未命名.txt\" with the content \"hello world!\". How can I assist you with your IoT devices or automation workflows in the Cereb IoT app today?"
   * }
   * [
   *   {
   *     "filename": "未命名.txt",
   *     "content": "hello world!"
   *   }
   * ]]
   */
  content = [];
  contentType = MessageContentType.TEXT;
  sourceType = MessageSourceType.USER;
  timestamp = Date.now();
  id = 0;
  avatarUrl = "";
  /**
   * 最后合并的消息内容
   */
  lastContent = "";
  /**
   * 当前消息是否已经完整（对于流式消息）
   */
  isCompleted = false;
  files = [];
  /**
   * 工具交互记录
   * @type {Array<{name: string, id: string, args: Object, replyTo: number}>}
   */
  tool_interactions = [];

  /**
   * 消息模型构造函数
   * @param {Partial<MessageModel>?} options
   */
  constructor(options) {
    if (options) {
      Object.assign(this, options);
    }
  }
}
